import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Phone, Mail, Send } from "lucide-react";

export default function FloatingChatButton() {
  const [isOpen, setIsOpen] = useState(false);

  const contactOptions = [
    {
      icon: MessageCircle,
      label: "WhatsApp",
      color: "bg-green-500 hover:bg-green-600",
      href: "https://wa.me/61421815593?text=Hi%20Dream%20Makers!%20I'm%20interested%20in%20booking%20a%20session.",
    },
    {
      icon: Phone,
      label: "Call Us",
      color: "bg-blue-500 hover:bg-blue-600",
      href: "tel:+61421815593",
    },
    {
      icon: Mail,
      label: "Email",
      color: "bg-purple-500 hover:bg-purple-600",
      href: "mailto:dreammakersrecordinggroup@gmail.com?subject=Booking%20Inquiry",
    },
  ];

  return (
    <div className="fixed bottom-24 right-6 z-50">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            className="absolute bottom-16 right-0 mb-2 space-y-2"
          >
            {contactOptions.map((option, index) => (
              <motion.a
                key={option.label}
                href={option.href}
                target={option.label === "WhatsApp" ? "_blank" : "_self"}
                rel="noopener noreferrer"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center gap-3 ${option.color} text-white px-4 py-3 rounded-full shadow-lg transition-all whitespace-nowrap`}
              >
                <option.icon className="w-5 h-5" />
                <span className="font-medium text-sm">{option.label}</span>
              </motion.a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all ${
          isOpen 
            ? "bg-dream-surface border border-dream-border" 
            : "bg-gradient-to-r from-dream-violet to-pink-500"
        }`}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
            >
              <X className="w-6 h-6 text-white" />
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              className="relative"
            >
              <MessageCircle className="w-6 h-6 text-white" />
              {/* Ping animation */}
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-ping" />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Tooltip */}
      {!isOpen && (
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 2 }}
          className="absolute bottom-4 right-20 bg-dream-surface border border-dream-border text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap shadow-lg"
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-2 h-2 bg-dream-surface border-r border-b border-dream-border rotate-[-45deg]" />
          Need help? Chat with us!
        </motion.div>
      )}
    </div>
  );
}
